import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import Helmet from 'react-helmet'
import { isEmpty } from 'lodash'

import { Loader } from '@/components/loaders/Loader'
import { toast } from '@/components/toasts/toast.helpers'
import { buildUserAttributes } from '@/utils/launchDarkly'
import { Content } from '@/components/layout/Content'
import { Wrapper } from '@/components/layout/Wrapper'
import { Sidebar } from '@/components/navigation/Sidebar/Sidebar'
import { unauthenticateUser } from '@/reducers/auth/currentUser.actions'
import {
  arePermissionsPending,
  hasAgentAssistAccess,
  hasPermission,
  loginUser,
  PERMISSIONS,
  RBAC_MODE_OFF,
} from '@/reducers/auth/currentUser.redux'
import { fetchCurrentOrganization } from '@/reducers/auth/currentOrganization.actions'
import { FloatingCoachingPanel } from '@/views/RealtimeCoaching/Alerts/FloatingCoachingPanel'
import NewVersionBanner from '@/components/banners/NewVersionBanner'
import { SupportAccessBanner } from '@/components/banners/SupportAccessBanner'
import { PermissionsUnavailableBanner } from '@/components/banners/PermissionsUnavailableBanner'
import { MetricsJobFunctionModal } from '@/components/layout/modals/MetricsJobFunctionModal'

import { isKodiRoute } from './routing.helpers'

import faviconLight from '../assets/images/favicon-light.png'
import faviconDark from '../assets/images/favicon-dark.png'

export const ACCESS_DENIED_MESSAGE = "You don't have access to that page."
export const ACCESS_DENIED_TOAST_ID = 'access-denied'

// A guard that bounces silently looks like a broken link: the user clicks,
// lands on Live Listen and can't tell why. Say so once, then redirect.
const AccessDeniedRedirect = () => {
  useEffect(() => {
    toast.info(ACCESS_DENIED_MESSAGE, { id: ACCESS_DENIED_TOAST_ID })
  }, [])

  return <Redirect to="/" />
}

const ProtectedRouteComponent = ({
  component: Component,
  adminOnly,
  cloudAccessRoute,
  noBaltoAdminException,
  dataGrid,
  dataGridColumn,
  qaRoute,
  qaCopilotRoute,
  promptEditorRoute,
  toolsEditorRoute,
  outboundCampaignsRoute,
  togoEditorRoute,
  togoAnalyticsRoute,
  alexInsightsRoute,
  supportAccessRoute,
  accessManagementRoute,
  requiredPermission,
  componentProps,
  ldClient,
  pageTitle,
  ...rest
}) => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const currentUser = useSelector((state) => state.currentUser)
  const {
    token,
    organizationid: currentOrganizationId,
    balto_cloud_access: cloudAccess,
  } = currentUser
  const currentOrganization = useSelector((state) => state.currentOrganization)
  const can = (permission, legacyAllowed) =>
    hasPermission(currentUser, currentOrganization, permission, legacyAllowed)
  const { customBranding } = useSelector((state) => state.customBranding)
  const [checkedAuth, setCheckedAuth] = useState(() => !!token)
  const [checkedOrg, setCheckedOrg] = useState(() => !isEmpty(currentOrganization))

  const isBaltoAdmin = currentOrganizationId === 1
  // Kodi mounts its own owner-scoped SupportAccessBanner. Suppress the global
  // one here so the two don't fight over the single-org supportAccess slice
  // (redundant 60s polls + banner flicker). Excludes /kodi-usage.
  const onKodiRoute = isKodiRoute(location.pathname)
  const faviconUrl = customBranding?.faviconUrl
  const siteTitle = customBranding?.customBrandingDisplayName || 'Balto Cloud'
  const title = pageTitle ? `${pageTitle} | ${siteTitle}` : siteTitle

  useEffect(() => {
    if (!isEmpty(currentOrganization)) {
      setCheckedOrg(true)
    }

    if (!token) {
      const user = JSON.parse(localStorage.getItem('User'))

      if (user) {
        dispatch(loginUser(user))

        if (ldClient) {
          ldClient.identify(buildUserAttributes(user))
        }
      } else {
        dispatch(unauthenticateUser({ history, location, ldClient }))
      }
    }

    setCheckedAuth(true)
  }, [])

  useEffect(() => {
    const authenticateOrganization = async () => {
      await dispatch(fetchCurrentOrganization(currentOrganizationId))

      setCheckedOrg(true)
    }

    if (token && isEmpty(currentOrganization)) {
      authenticateOrganization()
    }
  }, [token, currentOrganization])

  if (customBranding.customBrandingTheme) {
    const { primary_color, secondary_color } = customBranding.customBrandingTheme

    document.documentElement.style.setProperty('--color-brand', primary_color)
    document.documentElement.style.setProperty('--color-brand-hover', secondary_color)
    document.documentElement.style.setProperty('--color-brand-control', primary_color)
    document.documentElement.style.setProperty('--color-brand-control-hover', secondary_color)
  } else {
    document.documentElement.style.removeProperty('--color-brand')
    document.documentElement.style.removeProperty('--color-brand-hover')
    document.documentElement.style.removeProperty('--color-brand-control')
    document.documentElement.style.removeProperty('--color-brand-control-hover')
  }

  // Don't render component before auth is checked
  if (!checkedAuth) {
    return <Loader page data-testid="protected-route-loading" />
  }

  // Redirect admin routes if not an admin
  if (adminOnly && !isBaltoAdmin) {
    return <AccessDeniedRedirect />
  }

  // Redirect Balto Cloud Access (manager) routes if user lacks cloud_access.
  // noBaltoAdminException drops the admin bypass for routes whose API enforces
  // Cloud Access with no exception either -- letting an admin through would
  // only land them on a page where every call 403s.
  if (cloudAccessRoute && !cloudAccess && (!isBaltoAdmin || noBaltoAdminException)) {
    return <AccessDeniedRedirect />
  }

  // Every guard below may read RBAC capabilities, which need the org (its
  // rbac_mode stands in until the permissions response reports the home org's)
  // and, under 'enforce', that response. Deciding earlier would bounce a user
  // who is allowed in.
  if (!checkedOrg || arePermissionsPending(currentUser, currentOrganization)) {
    return <Loader page data-testid="protected-route-loading" />
  }

  if (requiredPermission && !isBaltoAdmin && !can(requiredPermission)) {
    return <AccessDeniedRedirect />
  }

  // Redirect permissionless QA routes
  if (qaRoute && !can(PERMISSIONS.QUALITY_WRITE, currentUser.edit_qa) && !isBaltoAdmin) {
    return <AccessDeniedRedirect />
  }

  // Redirect permissionless QA Copilot routes (prompt editors also have access)
  if (
    qaCopilotRoute &&
    !can(PERMISSIONS.QUALITY_WRITE, currentUser.edit_qa_copilot) &&
    !can(PERMISSIONS.PROMPTS_WRITE) &&
    !isBaltoAdmin
  ) {
    return <AccessDeniedRedirect />
  }

  // Redirect permissionless Prompt Editor routes
  if (promptEditorRoute && !can(PERMISSIONS.PROMPTS_WRITE) && !isBaltoAdmin) {
    return <AccessDeniedRedirect />
  }

  // Redirect permissionless Tools Editor routes (requires tools_enabled on org AND tools_editor permission)
  if (
    toolsEditorRoute &&
    !isBaltoAdmin &&
    !(can(PERMISSIONS.TOOLS_WRITE) && currentOrganization.tools_enabled)
  ) {
    return <AccessDeniedRedirect />
  }

  // Redirect outbound campaign routes (requires outbound_campaigns_enabled on org AND
  // outbound_campaign_editor permission). Mirrors the Sidebar nav gate.
  if (
    outboundCampaignsRoute &&
    !isBaltoAdmin &&
    !(can(PERMISSIONS.OUTBOUND_CAMPAIGNS_WRITE) && currentOrganization.outbound_campaigns_enabled)
  ) {
    return <AccessDeniedRedirect />
  }

  // Redirect Agent Studio (voice-agent editor) routes — requires togo_enabled on org
  // AND the togo_editor permission. Mirrors the sidebar nav gate.
  if (
    togoEditorRoute &&
    !isBaltoAdmin &&
    !(can(PERMISSIONS.TOGO_WRITE) && currentOrganization.togo_enabled)
  ) {
    return <AccessDeniedRedirect />
  }

  // Redirect Togo Analytics route — the page hosts both the voice-agent and outbound
  // analytics tabs, so either org flag grants access.
  if (
    togoAnalyticsRoute &&
    !isBaltoAdmin &&
    !currentOrganization.togo_enabled &&
    !currentOrganization.outbound_campaigns_enabled
  ) {
    return <AccessDeniedRedirect />
  }

  // Redirect Agentic Insights routes (requires alex_insights_enabled on org OR org 1)
  if (alexInsightsRoute && !isBaltoAdmin && !currentOrganization.alex_insights_enabled) {
    return <AccessDeniedRedirect />
  }

  // Redirect Support Access route for users without manage_support_access permission
  // (org-1 users do not need this page — they see the banner instead)
  if (supportAccessRoute && (!can(PERMISSIONS.SUPPORT_ACCESS_WRITE) || isBaltoAdmin)) {
    return <AccessDeniedRedirect />
  }

  // Access Management (RBAC seats) is reachable once the org has opted into RBAC
  // (rbac_mode set and not 'off'), for a seat with users:read; it sits beside the
  // legacy /users admin. Balto admins are NOT exempt: the page is org-scoped and
  // only meaningful for an RBAC-enabled org.
  if (
    accessManagementRoute &&
    (!currentOrganization.rbac_mode ||
      currentOrganization.rbac_mode === RBAC_MODE_OFF ||
      !can(PERMISSIONS.USERS_READ))
  ) {
    return <AccessDeniedRedirect />
  }

  return (
    <Route {...rest}>
      <NewVersionBanner />
      <PermissionsUnavailableBanner />
      {!onKodiRoute && <SupportAccessBanner />}
      <MetricsJobFunctionModal />
      <Helmet>
        <link href={faviconUrl || faviconLight} rel="icon" media="(prefers-color-scheme: light)" />
        <link href={faviconUrl || faviconDark} rel="icon" media="(prefers-color-scheme: dark)" />
        <title>{title}</title>
      </Helmet>
      <Wrapper>
        <Sidebar ldClient={ldClient} />
        <Content dataGrid={dataGrid} dataGridColumn={dataGridColumn}>
          <Component location={location} history={history} {...componentProps} />
        </Content>
        {/* Live listen/chat/transcript overlay (floats on the right). RTA alerts
            now go to the notification bell; this is only the live affordances.
            Gated via the same check as the sidebar Agent Assist launcher. */}
        {hasAgentAssistAccess(currentOrganization) && <FloatingCoachingPanel />}
      </Wrapper>
    </Route>
  )
}

export const ProtectedRoute = withLDConsumer()(ProtectedRouteComponent)
